import { useEffect, useRef } from 'react'

type Callbacks = {
    onStart: () => void
    onEnd: (endCause: string) => void
}

/**
 * Observes a component's initial mount or data-reset cycle (e.g., new query args like date range).
 *
 * The loading session starts synchronously during any render phase that initiates a new loading state, and ends
 * once the component completes its rendering with the new data. The session is aborted if a new loading cycle begins
 * before the current one has ended (i.e., `loadingKey` change), or if the component unmounts.
 *
 * @param isLoading - A boolean indicating whether the loading session is active.
 * @param loadingKey - A unique key representing the current loading session.
 * @param callbacks - An object containing callback functions for `start` and `end` events.
 */
export const useLoadingObserver = (isLoading: boolean, loadingKey: string, callbacks: Callbacks) => {
    // Track whether a loading session is currently active
    const loadingSessionRef = useRef(false)

    // Keep callbacks fresh without adding them to effect deps (avoids stale closure)
    const callbacksRef = useRef(callbacks)
    callbacksRef.current = callbacks

    // Track whether the loading key has changed
    const prevLoadingKeyRef = useRef(loadingKey)
    const hasLoadingKeyChanged = prevLoadingKeyRef.current !== loadingKey

    // Abort the current loading session if the loading key has changed
    // (e.g. if the user navigates to a new date range or sub-page while loading that re-renders the current component)
    if (hasLoadingKeyChanged) {
        if (loadingSessionRef.current) {
            callbacksRef.current.onEnd('loadingKeyChanged')
            loadingSessionRef.current = false
        }
        prevLoadingKeyRef.current = loadingKey
    }

    // Start the new loading session
    if (isLoading && !loadingSessionRef.current) {
        callbacksRef.current.onStart()
        loadingSessionRef.current = true
    }

    // End the loading session when loading finishes
    useEffect(() => {
        if (!isLoading && loadingSessionRef.current) {
            callbacksRef.current.onEnd('loadingFinished')
            loadingSessionRef.current = false
        }
    }, [isLoading])

    // Cleanup on unmount (e.g. if the user navigates away while loading)
    useEffect(
        () => () => {
            if (loadingSessionRef.current) {
                callbacksRef.current.onEnd('componentUnmounted')
                loadingSessionRef.current = false
            }
        },
        []
    )
}
